import React, { useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import SydLabel, { hookFormErrorAdapter } from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import SydSwitch from '../../../../commonDesign/SydSwitch'
import SydSelect from '../../../../commonDesign/SydSelect'
import { useListAbundanceEngineViews, useListEnablementCodes } from '../../../../../api/abundanceEngine'
import SydAutocomplete from '../../../../commonDesign/SydAutocomplete'

function ViewDetailInputs () {
  const { control, formState } = useFormContext()

  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500)
    return () => clearTimeout(timer)
  }, [searchTerm, debouncedSearchTerm])

  const { data, isLoading } = useListEnablementCodes()
  const { data: views = [], isLoading: isLoadingViews, isFetching: isFetchingViews } = useListAbundanceEngineViews(
    {
      includes: { contents: false },
      page: 1,
      limit: 10,
      sort: [{ field: 'name', dir: 'asc' }],
      search: debouncedSearchTerm?.length ? debouncedSearchTerm : undefined
    },
    {
      mapper: data => data.rows ?? []
    }
  )

  return (
    <div className='flex flex-col gap-5'>
      <Controller
        name='path'
        control={control}
        rules={{ required: 'Path is required' }}
        render={({ field, fieldState }) => (
          <SydLabel
            label='Path'
            description='The unique path for this view, used in the URL.'
            error={hookFormErrorAdapter({ formState }, fieldState)}
          >
            <SydInput
              placeholder='unique-view-path'
              size='sm'
              required
              {...field}
            />
          </SydLabel>
        )}
      />

      <Controller
        name='name'
        control={control}
        rules={{ required: 'Name is required' }}
        render={({ field, fieldState }) => (
          <SydLabel
            label='Name'
            description='The name of the view, displayed in the navigation and title. (if not hidden)'
            error={hookFormErrorAdapter({ formState }, fieldState)}
          >
            <SydInput
              placeholder='Enter a name for this view...'
              size='sm'
              required
              {...field}
            />
          </SydLabel>
        )}
      />

      <Controller
        name='description'
        control={control}
        render={({ field, fieldState }) => (
          <SydLabel
            label='Description'
            description='A short description of the view.'
            error={hookFormErrorAdapter({ formState }, fieldState)}
          >
            <SydInput
              placeholder='Enter a description for this view...'
              size='sm'
              {...field}
            />
          </SydLabel>
        )}
      />

      <Controller
        name='isLive'
        control={control}
        render={({ field, fieldState }) => (
          <SydLabel
            label='Is Live?'
            description='Whether this view is live / accessible to end users.'
            error={hookFormErrorAdapter({ formState }, fieldState)}
          >
            <SydSwitch {...field} />
          </SydLabel>
        )}
      />

      <Controller
        name='showInNavigation'
        control={control}
        render={({ field, fieldState }) => (
          <SydLabel
            label='Show in Navigation?'
            description='Whether to show this view in the left sidebar menu.'
            error={hookFormErrorAdapter({ formState }, fieldState)}
          >
            <SydSwitch {...field} />
          </SydLabel>
        )}
      />

      <Controller
        name='hideTitle'
        control={control}
        render={({ field, fieldState }) => (
          <SydLabel
            label='Hide Title?'
            description='Whether to hide the title of the view when displayed to end users.'
            error={hookFormErrorAdapter({ formState }, fieldState)}

          >
            <SydSwitch {...field} />
          </SydLabel>
        )}
      />

      <Controller
        name='enablementCodeId'
        control={control}
        render={({ field, fieldState }) => (
          <SydLabel
            label='Enablement Code'
            description='The code required to enable this view.'
            error={hookFormErrorAdapter({ formState }, fieldState)}
          >
            <SydSelect
              placeholder={isLoading ? 'Loading enablement codes...' : 'Select an enablement code'}
              options={[
                ...(data?.enablementCodes || []).map(ec => ({
                  label: ec.enablementCode,
                  value: ec.enablementCodeId
                })),
                { label: '<Not Assigned>', value: null }
              ]}
              isClearable
              isLoading={isLoading}
              minWidth='0'
              {...field}
            />
          </SydLabel>
        )}
      />

      <Controller
        name='disabledViewId'
        control={control}
        render={({ field, fieldState }) => (
          <SydLabel
            label='Fallback View (when disabled)'
            description='The view to show when this view is disabled. (if any)'
            error={hookFormErrorAdapter({ formState }, fieldState)}
          >
            <SydAutocomplete
              placeholder={isLoadingViews || isFetchingViews ? 'Loading views...' : 'Select a fallback view'}
              options={(views || []).map(v => ({
                label: v.name,
                value: v.viewId,
                subLabel: v.path
              }))}
              isClearable
              isLoading={isLoadingViews || isFetchingViews}
              onInputChange={(e) => setSearchTerm(e.target.value)}
              {...field}
            />
          </SydLabel>
        )}
      />
    </div>
  )
}

export default ViewDetailInputs
